import { AnimatorSystem, AwaitIterators, PlayAnimation, StartAnimationClip } from 'System/Animation/AnimationHelper';
import WorldSystem from 'System/World/WorldSystem';
import { delay } from 'Utility/Task/TaskQueue';
import ITask from 'Utility/Task/ITask';
import { getLayout } from 'Utility/layout/LayoutHelper';
import Renderable from 'Component/Renderable';
import {
    Alpha,
    DragonbonesDefault,
    DragonbonesFadeIn,
    OverlayFadeIn,
    OverlayFadeOut,
    RewardCoins,
} from 'System/Animation/Clip';
import GameObject, { GameObjectEvent } from 'GameObject/GameObject';
import { IRewardData } from 'Utility/RewardHelper';
import { RenderSystemEvent } from 'System/Render/RenderSystem';
import RelativePositionComponent from 'Component/Render/RelativePosition';
import Point from 'Utility/Point';
import Overlay from 'Prototype/UI/Overlay';
import { AnimationClipEvent } from 'System/Animation/AnimationClip';
import { EasterGiftLayout } from 'Layouts/Events/easter_2025/EasterGiftLayout';

export default class EasterGiftTask implements ITask<WorldSystem> {
    private reward: IRewardData;
    private previousCoins: number;

    public constructor(reward: IRewardData, previousCoins: number) {
        this.reward = reward;
        this.previousCoins = previousCoins;
    }

    public *execute(system: AnimatorSystem): IterableIterator<any> {
        const state = system.store.getState();

        const overlay = GameObject.Instantiate(Overlay, { alpha: 0.9, preventInteraction: true });
        system.animator.addChild(overlay);
        const layout = getLayout(EasterGiftLayout);
        layout.root.addComponent(RelativePositionComponent);
        layout.root.getComponent(RelativePositionComponent).relativePosition = new Point(0.5, 0.5);
        layout.root.getComponent(Renderable).alpha = 0;
        layout.gift.getComponent(Renderable).alpha = 0;
        system.animator.addChild(layout.root);

        system.scene.emit(RenderSystemEvent.BRING_TO_FRONT, system.animator);

        let hasSelected: boolean = false;
        const setHasSelected = () => {
            hasSelected = true;
        };
        overlay.once(GameObjectEvent.SELECTED, setHasSelected);
        // Show overlay
        yield* AwaitIterators([
            PlayAnimation(new DragonbonesFadeIn(layout.gift, { animationName: 'appear', playTimes: 1, concat: false })),
            PlayAnimation(new OverlayFadeIn(overlay)),
            PlayAnimation(new Alpha(layout.root, { startAlpha: 0, endAlpha: 1, duration: 500 })),
        ]);

        // Wait for selected
        while (!hasSelected) {
            yield;
        }

        StartAnimationClip(new Alpha(layout.text, { startAlpha: 1, endAlpha: 0, duration: 350 }));

        yield* delay(75);

        hasSelected = false;
        overlay.once(GameObjectEvent.SELECTED, setHasSelected);

        // Open gift
        let isOpenAnimationComplete = false;
        const openAnimationClip = new DragonbonesDefault(layout.gift, { animationName: 'open', playTimes: 1 });
        openAnimationClip.once(AnimationClipEvent.DESTROY, () => {
            isOpenAnimationComplete = true;
        });
        StartAnimationClip(openAnimationClip);
        while (!hasSelected && !isOpenAnimationComplete) {
            yield;
        }
        overlay.off(GameObjectEvent.SELECTED, setHasSelected);

        yield* AwaitIterators([
            PlayAnimation(new OverlayFadeOut(overlay)),
            PlayAnimation(new Alpha(layout.root, { startAlpha: 1, endAlpha: 0, duration: 500 })),
        ]);

        yield* PlayAnimation(
            new RewardCoins(system.animator, {
                previousValue: this.previousCoins,
                scene: system.scene,
                value: this.reward.value,
            })
        );

        layout.root.destroy();
        overlay.destroy();
    }
}
